import { useEffect, useState } from 'react'
import { apiURL } from '../../../../utilities/URLs'
import { account } from '../../../../classes/Account'
import { useNavigate, useParams } from 'react-router-dom'
import { refreshAccessToken } from '../../../../utilities/extensions'
import { CheckCircle, Download, Mail } from 'lucide-react'

export default function ShippingLabelPage() {
  const { applicationId } = useParams()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [emailSent, setEmailSent] = useState(false)
  const [downloadURL, setDownloadURL] = useState('')
  const [error, setError] = useState('')

  useEffect(() => {
    const handleGetPacket = async () => {
      try {
        const packetResponse = await fetch(`${apiURL}/api/applications/${applicationId}/packet/shipping-label/`, {
          method: 'GET',
          headers: {
            'Authorization': account.accessToken
          }
        })
        const packetData = await packetResponse.json()
        if (packetData.response.error) {
          if (packetData.response.error === 'Failed To Authenticate Token.') {
            const refreshedToken = await refreshAccessToken()
            refreshedToken && refreshedToken.length > 0 ? await handleGetPacket() : navigate('/', { state: { expired: true } })
          } else {
            setError(packetData.response.error)
            setLoading(false)
          }
        } else if (packetData.response.emailSent) {
          setEmailSent(true)
          if (packetData.response.downloadURL)
            setDownloadURL(packetData.response.downloadURL)
          setLoading(false)
        }
      } catch (err) {
        setError('An error occurred. Please try again.')
        setLoading(false)
      }
    }

    handleGetPacket()
  }, [applicationId, navigate])

  if (loading) {
    return (
      <div className='w-full h-full flex flex-col items-center justify-center gap-4 transition-colors duration-300'>
        <div className='ease-linear rounded-full border-[6px] border-t-[6px] border-gray-200 dark:border-slate-700 border-t-ertc-primary dark:border-t-ertc-primary dark:border-t-ertc-primary h-12 w-12 animate-spin'></div>
        <p className='text-lg text-center text-gray-700 dark:text-slate-300'>Generating your packet...</p>
        <p className='text-sm text-center text-gray-500 dark:text-slate-400'>This may take a moment</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className='w-full h-full flex flex-col items-center justify-center gap-4 p-6 transition-colors duration-300'>
        <div className='max-w-md w-full bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-500/20 rounded-lg p-6 text-center'>
          <p className='text-red-600 dark:text-red-400 font-medium'>Something went wrong</p>
          <p className='text-red-500 dark:text-red-400/80 text-sm mt-2'>{error}</p>
          <button
            onClick={() => navigate(`/applications/${applicationId}/packet`)}
            className='mt-4 px-6 py-2 bg-ertc-primary text-white rounded-lg cursor-pointer'
          >
            Go Back
          </button>
        </div>
      </div>
    )
  }

  if (emailSent) {
    return (
      <div className='w-full min-h-full flex flex-col items-center justify-center p-6 transition-colors duration-300'>
        <div className='max-w-lg w-full bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl p-8 shadow-lg text-center transition-colors duration-300'>
          <div className='w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-6'>
            <CheckCircle className='text-green-600 dark:text-green-500' size={48} />
          </div>
          <h1 className='text-2xl font-bold text-gray-900 dark:text-white mb-4'>Email Sent!</h1>
          <div className='flex items-center justify-center gap-2 text-gray-600 dark:text-slate-300 mb-6'>
            <Mail size={20} />
            <p>Check your email for the download link</p>
          </div>
          <p className='text-gray-500 dark:text-slate-400 mb-8'>
            We've sent your ERC packet to your registered email address.
            You can also download it below, or use the link in your email.
          </p>
          <div className='bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800/50 rounded-lg p-4 mb-6'>
            <p className='text-blue-700 dark:text-blue-400 text-sm'>
              <strong>Tip:</strong> If you don't see the email, please check your spam or junk folder.
            </p>
          </div>
          {downloadURL && (
            <a
              href={downloadURL}
              target='_blank'
              rel='noopener noreferrer'
              className='w-full mb-3 px-6 py-3 flex items-center justify-center gap-2 bg-white dark:bg-slate-700 border-2 border-ertc-primary text-ertc-primary dark:text-ertc-primary font-bold rounded-lg hover:bg-ertc-primary/5 dark:hover:bg-ertc-primary/10 transition-all min-h-[44px]'
            >
              <Download size={20} aria-hidden='true' />
              Download Packet
            </a>
          )}
          <button
            type='button'
            onClick={() => navigate(`/applications/${applicationId}/document-center`)}
            className='w-full px-6 py-3 bg-ertc-primary text-white font-bold rounded-lg hover:bg-opacity-90 transition-all cursor-pointer min-h-[44px]'
          >
            Upload Documents
          </button>
        </div>
      </div>
    )
  }

  return null
}